import React, { useState, useEffect } from 'react';
import styles from './spql-top-header.module.sass';
import { Button, Checkbox, Input, Modal, Tooltip } from 'antd';
import { ReactComponent as download_icon } from '../../../assets/icons/Download.svg';
import { ReactComponent as copy_icon } from '../../../assets/icons/Copy.svg';
import { ReactComponent as columns_icon } from '../../../assets/icons/Columns.svg';
import { ReactComponent as down_icon } from '../../../assets/Down.svg';
import { ReactComponent as check_icon } from '../../../assets/icons/CheckOutline.svg';
import { Typography, Dropdown, Menu } from 'antd';
import Icon from '@ant-design/icons';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import {
  getSPQLSelectedColumns,
  getSPQLAvailableFields,
} from '../../../redux/selectors/deep_scan';
import {
  showNotification,
  infoNotification,
  warningNotification,
} from '../../Notifications';
import { JSONViewer } from '../../JSONViewer';

export const MIN_SELECTED_COLUMNS = 5;
export const MAX_SELECTED_COLUMNS = 15;

const { Title } = Typography;

const SPQLMenu = styled.div`
  li.ant-dropdown-menu-item {
    padding: 0;
  }
  .ant-dropdown-menu-item-active {
    background: #1c3138;
  }
`;

function SPQLColumnUnselected({
  column,
  selectedFields,
  changeSPQLSelectedColumns,
  ...props
}) {
  const handleColumnClick = () => {
    if (selectedFields.length >= MAX_SELECTED_COLUMNS) {
      showNotification(
        warningNotification({
          statusCode: 400,
          message: `You cannot have more than ${MAX_SELECTED_COLUMNS} columns in the table header`,
        }),
      );
      return;
    }
    const newSelectedFields = [...selectedFields, column];
    changeSPQLSelectedColumns(newSelectedFields);
  };

  return (
    <div
      className={styles['spql-column-dropdown']}
      onClick={handleColumnClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '5px 8px 5px 16px',
      }}
    >
      <span>{column}</span>
      <span
        style={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}
      >
        <Checkbox style={{ padding: 4 }} />
      </span>
    </div>
  );
}

function SPQLColumnSelected({
  column,
  selectedFields,
  changeSPQLSelectedColumns,
  ...props
}) {
  const CheckIcon = (props) => <Icon component={check_icon} {...props} />;
  const handleColumnClick = () => {
    if (selectedFields.length <= MIN_SELECTED_COLUMNS) {
      showNotification(
        warningNotification({
          statusCode: 400,
          message: `You cannot have less than ${MIN_SELECTED_COLUMNS} columns in the table header`,
        }),
      );
      return;
    }
    const newSelectedFields = selectedFields.filter((field) => {
      return field !== column;
    });
    changeSPQLSelectedColumns(newSelectedFields);
  };

  return (
    <div
      onClick={handleColumnClick}
      className={styles['spql-column-dropdown']}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '5px 8px 5px 16px',
      }}
    >
      <span>{column}</span>
      <span
        style={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}
      >
        <CheckIcon style={{ padding: 4, fontSize: 16 }} />
      </span>
    </div>
  );
}

function SPQLColumnSelectorMenu({
  changeSPQLSelectedColumns,
  setDropdownVisible,
  ...props
}) {
  const defaultSelectedFields = useSelector(getSPQLSelectedColumns);
  const allFields = useSelector(getSPQLAvailableFields);

  const [searchTerm, setSearchTerm] = useState('');

  const searchableColumns = allFields.filter((column) => {
    return (
      column.includes(searchTerm) && !defaultSelectedFields.includes(column)
    );
  });

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <SPQLMenu>
      <Menu
        style={{ width: 280, background: '#1C3138' }}
        onMouseLeave={() => setDropdownVisible(false)}
      >
        <Menu.Item key="0">
          <div style={{ padding: 8 }}>
            <Input onChange={handleSearch} placeholder="Search field names" />
          </div>
          <div>
            <div
              style={{
                paddingLeft: 16,
                display: 'flex',
                alignItems: 'center',
                height: 32,
                background: '#0B171B',
              }}
            >
              Selected field names
            </div>
            <div style={{ height: 200, overflowY: 'auto' }}>
              {defaultSelectedFields.map((column, idx) => (
                <SPQLColumnSelected
                  selectedFields={defaultSelectedFields}
                  changeSPQLSelectedColumns={changeSPQLSelectedColumns}
                  key={column}
                  column={column}
                />
              ))}
            </div>
          </div>
          <div>
            <div
              style={{
                paddingLeft: 16,
                display: 'flex',
                alignItems: 'center',
                height: 32,
                background: '#0B171B',
              }}
            >
              Field names
            </div>
            <div style={{ height: 200, overflowY: 'auto' }}>
              {searchableColumns.map((column, idx) => (
                <SPQLColumnUnselected
                  selectedFields={defaultSelectedFields}
                  changeSPQLSelectedColumns={changeSPQLSelectedColumns}
                  key={column}
                  column={column}
                />
              ))}
            </div>
          </div>
        </Menu.Item>
      </Menu>
    </SPQLMenu>
  );
}

function SPQLColumnSelector({ changeSPQLSelectedColumns, ...props }) {
  const ColumnsIcon = (props) => <Icon component={columns_icon} {...props} />;
  const [dropdownVisible, setDropdownVisible] = React.useState(false);

  return (
    <Dropdown
      open={dropdownVisible}
      overlay={
        <SPQLColumnSelectorMenu
          changeSPQLSelectedColumns={changeSPQLSelectedColumns}
          setDropdownVisible={setDropdownVisible}
        />
      }
    >
      <Button
        onClick={() => setDropdownVisible(true)}
        style={{ color: 'white', marginLeft: 8, border: 0 }}
        icon={<ColumnsIcon style={{ fontSize: 16 }} />}
      />
    </Dropdown>
  );
}

function CopySPQLResults({ columns, selectedRows, scanData, ...props }) {
  const CopyIcon = (props) => <Icon component={copy_icon} {...props} />;

  const handleCopy = () => {
    let data = '';
    if (selectedRows?.length > 0) {
      data = scanData.filter((row) => selectedRows.includes(row.key));
    } else {
      data = scanData;
    }
    const text = data
      .map((row) => {
        return columns
          .map((column) => {
            if (column === 'favicon_icons') {
              return row[column].join(', ');
            }
            return row[column];
          })
          .join(',');
      })
      .join('\n');
    navigator.clipboard.writeText(text);
    showNotification(
      infoNotification({
        statusCode: 100,
        message: 'Copied to clipboard',
      }),
    );
  };
  return (
    <>
      <Tooltip title="Copy all visible or selected results raw data">
        <Button
          onClick={handleCopy}
          style={{ marginLeft: 8, color: 'white', border: 0 }}
          ghost
          icon={<CopyIcon style={{ fontSize: 16 }} />}
        ></Button>
      </Tooltip>
    </>
  );
}

function ExportSPQLResults({ columns, selectedRows, scanData, ...props }) {
  const DownloadIcon = (props) => <Icon component={download_icon} {...props} />;
  const [modalVisible, setModalVisible] = useState(false);

  const handleExportCSV = () => {
    let data = '';
    if (selectedRows?.length > 0) {
      data = scanData.filter((row) => selectedRows.includes(row.key));
    } else {
      data = scanData;
    }
    const text = data
      .map((row) => {
        return columns
          .map((column) => {
            if (column === 'favicon_icons') {
              return row[column].join(', ');
            }
            if (typeof row[column] === 'string' && row[column]?.includes(',')) {
              return `"${row[column]}"`;
            }
            if (Array.isArray(row[column])) {
              return `"${row[column].join(', ')}"`;
            }
            return row[column];
          })
          .join(',');
      })
      .join('\n');
    const element = document.createElement('a');
    const file = new Blob([columns.join(',') + '\n' + text], {
      type: 'text/plain',
    });
    element.href = URL.createObjectURL(file);
    element.download = 'results.csv';
    document.body.appendChild(element);
    element.click();
    setModalVisible(false);
  };

  const handleExportJSON = () => {
    let data = '';
    if (selectedRows?.length > 0) {
      data = scanData
        .filter((row) => selectedRows.includes(row.key))
        .map((row) => {
          return Object.keys(row)
            .filter((column) => columns.includes(column))
            .reduce((obj, key) => {
              obj[key] = row[key];
              return obj;
            }, {});
        });
    } else {
      data = scanData.map((row) => {
        return Object.keys(row)
          .filter((column) => columns.includes(column))
          .reduce((obj, key) => {
            obj[key] = row[key];
            return obj;
          }, {});
      });
    }
    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(data)], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'results.json';
    document.body.appendChild(element);
    element.click();
    setModalVisible(false);
  };

  return (
    <>
      <Tooltip title="Export all visible or selected results raw data">
        <Button
          onClick={() => setModalVisible(true)}
          style={{ marginLeft: 8, color: 'white', border: 0 }}
          ghost
          icon={<DownloadIcon style={{ fontSize: 16 }} />}
        ></Button>
      </Tooltip>
      {modalVisible && (
        <Modal
          cancelButtonProps={{ style: { display: 'none' } }}
          okButtonProps={{ style: { display: 'none' } }}
          open={modalVisible}
          title={'Export'}
          onCancel={() => setModalVisible(false)}
        >
          <Title style={{ textAlign: 'center', color: 'white' }} level={5}>
            Export all visible or selected results raw data
          </Title>
          <div
            style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}
          >
            <Button onClick={handleExportCSV} style={{ marginRight: 16 }}>
              CSV
            </Button>
            <Button onClick={handleExportJSON}>JSON</Button>
          </div>
        </Modal>
      )}
    </>
  );
}

function BasicRawData({ scanData, ...props }) {
  const [modalVisible, setModalVisible] = useState(false);
  const handleCopy = () => {
    const text = JSON.stringify(scanData, null, 2);
    navigator.clipboard.writeText(text);
    showNotification(
      infoNotification({
        statusCode: 100,
        message: 'Copied to clipboard',
      }),
    );
    setModalVisible(false);
  };

  return (
    <>
      <Button onClick={() => setModalVisible(true)}>Basic Raw Data</Button>
      {modalVisible && (
        <Modal
          onOk={handleCopy}
          okText={'Copy Raw Data'}
          open={modalVisible}
          width={800}
          onCancel={() => setModalVisible(false)}
          title="Basic raw data"
        >
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <JSONViewer data={scanData} />
          </div>
        </Modal>
      )}
    </>
  );
}

function SPQLActionHeader({
  scanData,
  selectedRows,
  setSelectedRows,
  changeSPQLSelectedColumns,
  ...props
}) {
  const columns = useSelector(getSPQLSelectedColumns);
  return (
    <div className={styles['spql-action-header-container']}>
      <div style={{ marginRight: 16 }}>
        <Checkbox
          onChange={(e) => {
            const checked = e.target.checked;
            if (checked) {
              setSelectedRows(scanData.map((row) => row.key));
              return;
            }
            setSelectedRows([]);
          }}
          indeterminate={
            selectedRows?.length > 0 && selectedRows?.length < scanData?.length
          }
          checked={selectedRows?.length === scanData?.length}
          data-cy={'spqlTableHeaderCheckbox'}
          style={{ margin: 8 }}
        />
        <CopySPQLResults
          columns={columns}
          selectedRows={selectedRows}
          scanData={scanData}
        />
        <ExportSPQLResults
          columns={columns}
          selectedRows={selectedRows}
          scanData={scanData}
        />
        <SPQLColumnSelector
          changeSPQLSelectedColumns={changeSPQLSelectedColumns}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <BasicRawData scanData={scanData} />
      </div>
      <div className={styles['spql-action-header-vertical-line']}></div>
      <div className={styles['spql-action-header-results-section']}>
        <div>
          <span className={styles['spql-action-header-results-section-text']}>
            Total Results:
          </span>
          <span
            className={styles['sqpl-action-header-results-section-value-text']}
          >
            {props.resultsMetadata?.results_total_at_least}
          </span>
        </div>
        <div style={{ marginLeft: 32 }}>
          <span className={styles['spql-action-header-results-section-text']}>
            Results on current page:
          </span>
          <span
            className={styles['sqpl-action-header-results-section-value-text']}
          >
            {props.resultsMetadata?.results_returned}
          </span>
        </div>
      </div>
    </div>
  );
}

export function SPQLTableHeader({
  scanData,
  selectedRows,
  setSelectedRows,
  changeSPQLSelectedColumns,
  ...props
}) {
  return (
    <div className={styles['spql-table-top-header']}>
      <div>
        <SPQLActionHeader
          scanData={scanData}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          changeSPQLSelectedColumns={changeSPQLSelectedColumns}
          resultsMetadata={props.resultsMetadata}
        />
      </div>
    </div>
  );
}
